<template>
  <div class="column bg-white text-grey-8 rounded-borders no-wrap">
    <SAIStepper
      class="col-grow user-profile-stepper column"
      :value="activePage"
      :swipeable="false"
      @input="$emit('update:page', $event)"
      :flat="true"
    >
      <SAIStep :name="profileStepperPages.corporateEmail">
        <SAIUserProfileStepCorporateEmail
          class="scroll no-wrap"
          :error="errors.companyEmail ? errors.companyEmail.$error : false"
          :user="user"
          :disable="saving"
          :loading="saving"
          :name="name"
          @next="$emit('next', $event)"
          @update:user="$emit('update:user', $event)"
        />
      </SAIStep>
      <SAIStep :name="profileStepperPages.companyName">
        <SAIUserProfileStepCompanyName
          class="scroll no-wrap"
          :error="errors.companyName ? errors.companyName.$error : false"
          :user="user"
          :disable="saving"
          :loading="saving"
          @next="$emit('next', $event)"
          @update:user="$emit('update:user', $event)"
        />
      </SAIStep>
      <SAIStep :name="profileStepperPages.organisationSelection">
        <SAIUserProfileStepOrganisation
          class="scroll no-wrap"
          :error="
            errors.selectedOrganisation
              ? errors.selectedOrganisation.$error
              : false
          "
          :saving="saving"
          :loading="loading"
          :selected-organisation="selectedOrganisation"
          :user-organisations="userOrganisations"
          @next="$emit('next', $event)"
          @update:organisation="$emit('update:organisation', $event)"
        />
      </SAIStep>
      <SAIStep :name="profileStepperPages.postcode">
        <SAIUserProfileStepPostcode
          class="scroll no-wrap"
          :user="user"
          :error="errors.postcode ? errors.postcode.$error : errors.postcode"
          :disable="saving"
          :loading="saving"
          :header-img="postcodeImg"
          @next="$emit('next', $event)"
          @update:user="$emit('update:user', $event)"
        />
      </SAIStep>
      <SAIStep :name="profileStepperPages.contactNumber">
        <SAIUserProfileStepContactNumber
          class="scroll no-wrap"
          :user="user"
          :error="errors.contactNumber ? errors.contactNumber.$error : false"
          :disable="saving"
          :loading="saving"
          :header-img="contactImg"
          @next="$emit('next', $event)"
          @update:user="$emit('update:user', $event)"
        />
      </SAIStep>
      <slot />
      <template v-slot:navigation>
        <QStepperNavigation>
          <SAIPageDots
            v-if="displayDots"
            :dots="pageDots"
            @input="$emit('update:page', $event.id)"
          />
          <p
            v-if="canSkip"
            :class="[
              'text-body1  text-weight-medium text-center q-mt-lg',
              {
                'cursor-pointer text-primary': !saving,
                'text-grey-6': saving
              }
            ]"
            @click="$emit('skip')"
          >
            Skip
          </p>
        </QStepperNavigation>
      </template>
    </SAIStepper>
  </div>
</template>

<script>
import { QStepperNavigation } from 'quasar';
import SAIStepper from '../../atom/sai-stepper';
import SAIStep from '../../atom/sai-step';
import SAIPageDots from '../../molecule/sai-page-dots';
import SAIUserProfileStepContactNumber from '../sai-user-profile-step-contact-number';
import SAIUserProfileStepCorporateEmail from '../sai-user-profile-step-corporate-email';
import SAIUserProfileStepOrganisation from '../sai-user-profile-step-organisation';
import SAIUserProfileStepPostcode from '../sai-user-profile-step-postcode';
import SAIUserProfileStepCompanyName from '../sai-user-profile-step-company-name';

const USER_PROFILE_STEPPER_PAGES = {
  corporateEmail: 'corporateEmail',
  companyName: 'companyName',
  organisationSelection: 'organisationSelection',
  postcode: 'postcode',
  contactNumber: 'contactNumber'
};

export default {
  name: 'SaiUserProfileStepper',
  props: {
    /**
     * Determines if the user can skip a step
     */
    canSkip: {
      type: Boolean,
      default: true
    },
    displayDots: {
      type: Boolean,
      default: true
    },
    /**
     * True if data related to the user profile stepper is saving
     */
    saving: Boolean,
    /**
     * The name of the user
     */
    name: String,
    /**
     * The URL of the image to display in the post code page
     */
    postcodeImg: String,
    /**
     * The URL of the image to display in the contact page
     */
    contactImg: String,
    /**
     * True if any stepper page is loading
     */
    loading: Boolean,
    /**
     * The list of possible user organisations to choose from
     */
    userOrganisations: {
      type: Array,
      default: () => []
    },
    /**
     * The selected user organisation
     */
    selectedOrganisation: {
      type: Object,
      default: () => ({})
    },
    /**
     * The users information
     */
    user: {
      type: Object,
      default: () => ({
        companyEmail: undefined,
        noCorporateEmail: false,
        contactNumber: undefined,
        postcode: undefined
      })
    },
    /**
     * The Step that should be displayed.
     * It is required that this value match one of the defined USER_PROFILE_STEPPER_PAGES
     */
    activePage: {
      type: String,
      required: true
    },
    /**
     * Assumed to be following vuelidate object structures.
     */
    errors: {
      type: Object,
      default: () => ({
        companyEmail: {},
        selectedOrganisation: {},
        postcode: {},
        contactNumber: {}
      })
    },
    /**
     * Definitions for pages to be either valid or invalid.
     */
    validPages: {
      type: Object,
      default: () => ({
        [USER_PROFILE_STEPPER_PAGES.corporateEmail]: false,
        [USER_PROFILE_STEPPER_PAGES.companyName]: false,
        [USER_PROFILE_STEPPER_PAGES.organisationSelection]: false,
        [USER_PROFILE_STEPPER_PAGES.postcode]: false,
        [USER_PROFILE_STEPPER_PAGES.contactNumber]: false
      })
    },
    /**
     * Enum of the available profile stepper pages
     */
    profileStepperPages: {
      type: Object,
      default: () => USER_PROFILE_STEPPER_PAGES
    }
  },
  computed: {
    pageDots() {
      return Object.values(this.profileStepperPages).map((page) => {
        return {
          id: page,
          active: this.activePage === page,
          disable: this.saving || !this.validPages[page]
        };
      });
    }
  },
  components: {
    SAIStepper,
    SAIStep,
    SAIPageDots,
    QStepperNavigation,
    SAIUserProfileStepContactNumber,
    SAIUserProfileStepCompanyName,
    SAIUserProfileStepCorporateEmail,
    SAIUserProfileStepOrganisation,
    SAIUserProfileStepPostcode
  }
};
</script>

<style lang="sass" scoped>
.user-profile-stepper::v-deep
  .q-stepper__step-content,
  .q-stepper__step-inner
    height: 100%

  .q-stepper__step-inner
    padding-top: 0
</style>
